import type {ReactElement} from 'react';
import React from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {SocialPost} from '@Components/social-media/post.vo';
import {MetricsAvailability} from '@Components/social-media/social-platform-metrics.types';
import {getReadableAccounts} from '@Components/social-media/social-media.types';
import {getAccountMetricsAvailability, hasAnyMetricsForAccount} from '@Components/social-media/social-post-metrics.helpers';
import {Text, TextSize} from '@Components/text';
import styles from './social-post-metrics-per-account-breakdown.module.scss';

interface SocialPostMetricsPerAccountBreakdownProps {
  post: SocialPost;
}

export function SocialPostMetricsPerAccountBreakdown({post}: SocialPostMetricsPerAccountBreakdownProps): ReactElement<any> {
  return (
    <ul className={styles.breakdownContainer}>
      {post.accounts.map((account): ReactElement<any> => renderAccountRow(post, account))}
    </ul>
  );
}

const renderAccountRow = (post: SocialPost, account: SocialAccount): ReactElement<any> => {
  const accountHasMetrics = hasAnyMetricsForAccount(post, account.id);
  const availability = getAccountMetricsAvailability(post, account.id);
  const statusLabel = getStatusLabel(accountHasMetrics, availability);
  const statusColorClass = accountHasMetrics ? 'ct-success-primary' : 'ct-neutral-secondary';

  return (
    <li key={account.id} className={styles.breakdownRow}>
      <div className={styles.accountMeta}>
        <Text val={account.name} className={`${styles.accountName} ct-neutral-primary`} size={TextSize.XSMALL} bold />
        <Text val={getReadableAccounts(account.type)} className="ct-neutral-secondary" size={TextSize.XXSMALL} />
      </div>
      <Text val={statusLabel} className={`${styles.statusBadge} ${statusColorClass}`} size={TextSize.XXSMALL} />
    </li>
  );
};

const getStatusLabel = (accountHasMetrics: boolean, availability: MetricsAvailability): string => {
  if (accountHasMetrics) {
    return window.i18next.t('pmwjs_metrics_breakdown_available');
  }

  switch (availability) {
    case MetricsAvailability.DELETED_ON_PLATFORM:
      return window.i18next.t('pmwjs_metrics_breakdown_deleted_on_platform');
    case MetricsAvailability.ACCESS_REVOKED:
      return window.i18next.t('pmwjs_metrics_breakdown_access_revoked');
    case MetricsAvailability.PERMISSIONS_PENDING:
      return window.i18next.t('pmwjs_metrics_breakdown_permissions_pending');
    case MetricsAvailability.MAX_FAILURES_EXCEEDED:
      return window.i18next.t('pmwjs_metrics_breakdown_max_failures');
    case MetricsAvailability.NOT_SUPPORTED:
      return window.i18next.t('pmwjs_metrics_breakdown_not_supported');
    case MetricsAvailability.NOT_SUPPORTED_AFTER_WINDOW:
      return window.i18next.t('pmwjs_metrics_breakdown_window_closed');
    case MetricsAvailability.AVAILABLE:
    default:
      return window.i18next.t('pmwjs_metrics_breakdown_pending');
  }
};

export default SocialPostMetricsPerAccountBreakdown;
